import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tag, Plus, Trash2, Save, Loader2, BarChart3, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { LowCouponAlert } from '@/components/team/LowCouponAlert';

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

function CouponAnalyticsTable() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get('/admin/coupons/analytics')
      .then(r => setRows(r.data.rows || []))
      .catch(() => toast.error('Failed to load coupon analytics'));
  }, []);
  if (rows === null) return <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" /></div>;
  if (rows.length === 0) return <div className="py-6 text-center text-xs text-muted-foreground">No coupons yet.</div>;
  return (
    <Card className="bg-white border-stone/50" data-testid="coupon-analytics-table">
      <CardContent className="p-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-stone/30 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="pb-2">Code</th>
              <th className="pb-2 text-right">Redemptions</th>
              <th className="pb-2 text-right">Customers</th>
              <th className="pb-2 text-right">Gross</th>
              <th className="pb-2 text-right">Discount</th>
              <th className="pb-2 text-right">AOV</th>
              <th className="pb-2">Top customers</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-stone/10 align-top" data-testid={`coupon-analytics-row-${r.code}`}>
                <td className="py-2 font-mono text-[11px] text-ink">
                  {r.code}
                  {!r.is_active && <span className="ml-1 text-[9px] text-muted-foreground">(inactive)</span>}
                </td>
                <td className="py-2 text-right">{r.redemptions}</td>
                <td className="py-2 text-right">{r.unique_customers}</td>
                <td className="py-2 text-right">{fmt$(r.gross_cents)}</td>
                <td className="py-2 text-right text-[#5A1F2A]">-{fmt$(r.discount_cents)}</td>
                <td className="py-2 text-right">{fmt$(r.avg_order_cents)}</td>
                <td className="py-2 text-[10px] text-muted-foreground">
                  {(r.top_customers || []).slice(0, 3).map((c, i) => (
                    <div key={i}>{c.email || c.customer_id.slice(0, 8)} · {fmt$(c.spend_cents)} ({c.orders})</div>
                  ))}
                  {r.top_customers && r.top_customers.length === 0 && <span>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

export default function CouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [form, setForm] = useState({ code: '', description: '', discount_type: 'percentage', discount_value: 10, applies_to: 'all', usage_limit: null, per_customer_limit: 1, is_active: true, internal_only: false, allowed_emails: '' });
  // Aug 2026: inline edit dialog. code is read-only after creation to keep
  // any live coupon URLs / share links valid.
  const [editCtx, setEditCtx] = useState(null);  // { coupon, form: {...editable fields}, saving }

  useEffect(() => { loadCoupons(); }, []);

  const loadCoupons = async () => {
    try { const r = await api.get('/admin/coupons'); setCoupons(r.data); } catch {} finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.code.trim()) { toast.error('Code required'); return; }
    const payload = { ...form };
    // Admin enters fixed amounts in DOLLARS; the backend stores cents.
    if (form.discount_type === 'fixed_cents') {
      payload.discount_value = Math.round((parseFloat(form.discount_value) || 0) * 100);
    } else {
      payload.discount_value = parseInt(form.discount_value) || 0;
    }
    // Slice 6 (LH-01): comma-separated allowed_emails string → normalized array.
    payload.allowed_emails = (form.allowed_emails || '')
      .split(/[\s,]+/)
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
    try {
      await api.post('/admin/coupons', payload);
      toast.success('Coupon created');
      setShowCreate(false);
      setForm({ code: '', description: '', discount_type: 'percentage', discount_value: 10, applies_to: 'all', usage_limit: null, per_customer_limit: 1, is_active: true, internal_only: false, allowed_emails: '' });
      await loadCoupons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (id, val) => {
    try { await api.put(`/admin/coupons/${id}`, { is_active: val }); await loadCoupons(); } catch { toast.error('Failed'); }
  };

  const openEdit = (coupon) => {
    // Dollars-in-UI, cents-on-wire — mirror the create form's convention.
    const value = coupon.discount_type === 'fixed_cents'
      ? ((coupon.discount_value || 0) / 100).toFixed(2)
      : coupon.discount_value;
    setEditCtx({
      coupon,
      saving: false,
      form: {
        description: coupon.description || '',
        discount_type: coupon.discount_type || 'percentage',
        discount_value: value,
        applies_to: coupon.applies_to || 'all',
        usage_limit: coupon.usage_limit ?? null,
        per_customer_limit: coupon.per_customer_limit ?? 1,
        min_order_cents: coupon.min_order_cents ?? 0,
        max_discount_cents: coupon.max_discount_cents ?? null,
        valid_from: (coupon.valid_from || '').slice(0, 10),
        valid_until: (coupon.valid_until || '').slice(0, 10),
        is_active: coupon.is_active !== false,
        internal_only: !!coupon.internal_only,
        allowed_emails: (coupon.allowed_emails || []).join(', '),
      },
    });
  };

  const saveEdit = async () => {
    if (!editCtx) return;
    setEditCtx(ctx => ({ ...ctx, saving: true }));
    try {
      const f = editCtx.form;
      const payload = {
        description: f.description || null,
        discount_type: f.discount_type,
        discount_value: f.discount_type === 'fixed_cents'
          ? Math.round((parseFloat(f.discount_value) || 0) * 100)
          : parseInt(f.discount_value) || 0,
        applies_to: f.applies_to,
        usage_limit: f.usage_limit === '' || f.usage_limit == null ? null : parseInt(f.usage_limit),
        per_customer_limit: parseInt(f.per_customer_limit) || 1,
        min_order_cents: parseInt(f.min_order_cents) || 0,
        max_discount_cents: f.max_discount_cents === '' || f.max_discount_cents == null ? null : parseInt(f.max_discount_cents),
        valid_from: f.valid_from || null,
        valid_until: f.valid_until || null,
        is_active: !!f.is_active,
        internal_only: !!f.internal_only,
        allowed_emails: (f.allowed_emails || '').split(/[\s,]+/).map(e => e.trim().toLowerCase()).filter(Boolean),
      };
      await api.put(`/admin/coupons/${editCtx.coupon.id}`, payload);
      toast.success('Coupon updated');
      setEditCtx(null);
      await loadCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
      setEditCtx(ctx => ({ ...ctx, saving: false }));
    }
  };


  const deleteCoupon = async (id) => {
    try { await api.delete(`/admin/coupons/${id}`); toast.success('Deleted'); await loadCoupons(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="coupons-page">
      <LowCouponAlert linkToCoupons={false} />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-ink" /><h1 className="text-base font-semibold text-ink">Coupons</h1></div>
        <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => setShowAnalytics(!showAnalytics)} data-testid="toggle-analytics-btn"><BarChart3 className="h-3.5 w-3.5" /> {showAnalytics ? 'Hide' : 'View'} Analytics</Button>
        <Button size="sm" className="gap-1 text-xs" onClick={() => setShowCreate(true)} data-testid="create-coupon-btn"><Plus className="h-3.5 w-3.5" /> Create Coupon</Button>
      </div>

      {showAnalytics && <CouponAnalyticsTable />}

      {showCreate && (
        <Card className="bg-white border-stone/50 mb-4">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Code</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} className="mt-1 h-8 text-xs uppercase" data-testid="coupon-code-input" /></div>
              <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 h-8 text-xs" /></div>
              <div><Label className="text-xs">Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}><SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="fixed_cents">Fixed Amount</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs">Value ({form.discount_type === 'percentage' ? '%' : '$'})</Label><Input type="number" step={form.discount_type === 'percentage' ? '1' : '0.01'} value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} className="mt-1 h-8 text-xs" data-testid="coupon-value-input" /></div>
              <div><Label className="text-xs">Applies To</Label>
                <Select value={form.applies_to} onValueChange={v => setForm({...form, applies_to: v})}><SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="authentication">Authentication</SelectItem><SelectItem value="appraisal">Appraisal</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs">Usage Limit (total)</Label><Input type="number" value={form.usage_limit || ''} onChange={e => setForm({...form, usage_limit: parseInt(e.target.value) || null})} placeholder="Unlimited" className="mt-1 h-8 text-xs" /></div>
              <div><Label className="text-xs">Per-Customer Limit</Label><Input type="number" min="1" value={form.per_customer_limit} onChange={e => setForm({...form, per_customer_limit: parseInt(e.target.value) || 1})} className="mt-1 h-8 text-xs" data-testid="coupon-per-customer-input" /></div>
            </div>
            {/* Slice 6 (LH-01): internal-only guard for staff/testing coupons. */}
            <div className="rounded-md border border-stone/40 bg-canvas/50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-semibold text-ink">Internal-only coupon</Label>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    When enabled, only the emails below (or any staff email) can redeem this code.
                    Public users get a generic &quot;invalid coupon&quot; error.
                  </p>
                </div>
                <Switch
                  checked={form.internal_only}
                  onCheckedChange={v => setForm({...form, internal_only: v})}
                  data-testid="coupon-internal-only-switch"
                />
              </div>
              {form.internal_only && (
                <div>
                  <Label className="text-xs">Allowed emails (comma or newline separated)</Label>
                  <Input
                    value={form.allowed_emails}
                    onChange={e => setForm({...form, allowed_emails: e.target.value})}
                    placeholder="staff1@example.com, staff2@example.com"
                    className="mt-1 h-8 text-xs"
                    data-testid="coupon-allowed-emails-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Leave empty to allow any staff account.</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="text-xs gap-1" onClick={handleCreate}><Save className="h-3 w-3" /> Create</Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {coupons.length === 0 ? (
          <Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><Tag className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">No coupons yet.</p></CardContent></Card>
        ) : coupons.map(c => (
          <Card key={c.id} className="bg-white border-stone/50">
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-medium text-ink">{c.code}</span>
                <Badge variant="outline" className="text-[10px]">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${(c.discount_value/100).toFixed(2)}`}</Badge>
                <Badge variant="outline" className="text-[10px]">{c.applies_to}</Badge>
                {c.internal_only && (
                  <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-700" data-testid={`coupon-${c.code}-internal-badge`}>Internal</Badge>
                )}
                <span className="text-[10px] text-muted-foreground">Used: {c.usage_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ''}</span>
                <span className="text-[10px] text-muted-foreground">Per user: {c.per_customer_limit ?? 1}</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={c.is_active} onCheckedChange={v => toggleActive(c.id, v)} />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-ink"
                        onClick={() => openEdit(c)} data-testid={`edit-coupon-${c.code}`} title="Edit coupon">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={() => deleteCoupon(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit dialog (Aug 2026) — code field is read-only after creation. */}
      <Dialog open={!!editCtx} onOpenChange={(v) => !v && setEditCtx(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-coupon-dialog">
          <DialogHeader><DialogTitle className="text-sm">Edit coupon</DialogTitle></DialogHeader>
          {editCtx && (
            <div className="space-y-3 text-xs">
              <div>
                <Label className="text-xs">Code (read-only)</Label>
                <Input value={editCtx.coupon.code} disabled readOnly
                       className="mt-1 h-8 text-xs font-mono bg-stone/20 cursor-not-allowed"
                       data-testid="edit-coupon-code-readonly" />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Editing the code would invalidate any share links or emails already sent.
                </p>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={editCtx.form.description}
                       onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, description: e.target.value } }))}
                       className="mt-1 h-8 text-xs" data-testid="edit-coupon-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={editCtx.form.discount_type}
                          onValueChange={v => setEditCtx(c => ({ ...c, form: { ...c.form, discount_type: v } }))}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid="edit-coupon-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed_cents">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Value ({editCtx.form.discount_type === 'percentage' ? '%' : '$'})</Label>
                  <Input type="number"
                         step={editCtx.form.discount_type === 'percentage' ? '1' : '0.01'}
                         value={editCtx.form.discount_value}
                         onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, discount_value: e.target.value } }))}
                         className="mt-1 h-8 text-xs" data-testid="edit-coupon-value" />
                </div>
                <div>
                  <Label className="text-xs">Applies To</Label>
                  <Select value={editCtx.form.applies_to}
                          onValueChange={v => setEditCtx(c => ({ ...c, form: { ...c.form, applies_to: v } }))}>
                    <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="authentication">Authentication</SelectItem>
                      <SelectItem value="appraisal">Appraisal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Total usage limit</Label>
                  <Input type="number" placeholder="Unlimited"
                         value={editCtx.form.usage_limit ?? ''}
                         onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, usage_limit: e.target.value === '' ? null : parseInt(e.target.value) || null } }))}
                         className="mt-1 h-8 text-xs" data-testid="edit-coupon-usage-limit" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Used so far: {editCtx.coupon.usage_count || 0}
                  </p>
                </div>
                <div>
                  <Label className="text-xs">Per-customer limit</Label>
                  <Input type="number" min="1"
                         value={editCtx.form.per_customer_limit}
                         onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, per_customer_limit: parseInt(e.target.value) || 1 } }))}
                         className="mt-1 h-8 text-xs" data-testid="edit-coupon-per-customer" />
                </div>
                <div>
                  <Label className="text-xs">Min order ($)</Label>
                  <Input type="number" step="0.01"
                         value={((editCtx.form.min_order_cents || 0) / 100).toFixed(2)}
                         onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, min_order_cents: Math.round((parseFloat(e.target.value) || 0) * 100) } }))}
                         className="mt-1 h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Max discount ($)</Label>
                  <Input type="number" step="0.01" placeholder="No cap"
                         value={editCtx.form.max_discount_cents == null ? '' : ((editCtx.form.max_discount_cents || 0) / 100).toFixed(2)}
                         onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, max_discount_cents: e.target.value === '' ? null : Math.round((parseFloat(e.target.value) || 0) * 100) } }))}
                         className="mt-1 h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Valid from</Label>
                  <Input type="date" value={editCtx.form.valid_from}
                         onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, valid_from: e.target.value } }))}
                         className="mt-1 h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Valid until</Label>
                  <Input type="date" value={editCtx.form.valid_until}
                         onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, valid_until: e.target.value } }))}
                         className="mt-1 h-8 text-xs" data-testid="edit-coupon-valid-until" />
                </div>
              </div>
              <div className="rounded-md border border-stone/40 bg-canvas/50 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs font-semibold text-ink">Active</Label>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Uncheck to disable the coupon without deleting it.</p>
                  </div>
                  <Switch checked={editCtx.form.is_active}
                          onCheckedChange={v => setEditCtx(c => ({ ...c, form: { ...c.form, is_active: v } }))}
                          data-testid="edit-coupon-active-switch" />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-xs font-semibold text-ink">Internal-only</Label>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Only allowed emails (or staff) can redeem.</p>
                  </div>
                  <Switch checked={editCtx.form.internal_only}
                          onCheckedChange={v => setEditCtx(c => ({ ...c, form: { ...c.form, internal_only: v } }))} />
                </div>
                {editCtx.form.internal_only && (
                  <div>
                    <Label className="text-xs">Allowed emails</Label>
                    <Input value={editCtx.form.allowed_emails}
                           onChange={e => setEditCtx(c => ({ ...c, form: { ...c.form, allowed_emails: e.target.value } }))}
                           placeholder="staff1@example.com, staff2@example.com"
                           className="mt-1 h-8 text-xs" />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setEditCtx(null)} disabled={editCtx?.saving}>Cancel</Button>
            <Button size="sm" className="text-xs gap-1" onClick={saveEdit} disabled={editCtx?.saving} data-testid="edit-coupon-save-btn">
              {editCtx?.saving ? <><Loader2 className="h-3 w-3 animate-spin" />Saving…</> : <><Save className="h-3 w-3" />Save changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
